import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import {
  useGetSubscriptionsQuery,
  useUpdateSubscriptionMutation,
  useGetSepaExportDataMutation,
  useMarkBookingsCollectedMutation,
} from "../../../features/subscriptions/subscriptionsApiSlice";
import { useGetBankingAccountsQuery } from "../../../features/banking/bankingApiSlice";
import Iconify from "../../../components/iconify/Iconify";
import { buildPain008Xml } from "../../../components/sepa/SepaUtils";
import { useDispatch } from "react-redux";
import { setMessage } from "../../../features/message/messageSlice";

function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case "trial":
      return "info";
    case "active":
      return "success";
    case "cancelled":
      return "warning";
    case "expired":
      return "error";
    default:
      return "default";
  }
}

function getStatusLabel(status) {
  switch (status?.toLowerCase()) {
    case "trial":
      return "Testphase";
    case "active":
      return "Aktiv";
    case "cancelled":
      return "Gekündigt";
    case "expired":
      return "Abgelaufen";
    default:
      return status || "Unbekannt";
  }
}

function EditSubscriptionDialog({ open, onClose, subscription, onSave }) {
  const [formData, setFormData] = useState({
    status: subscription?.status || "active",
    plan: subscription?.plan || "basis",
    monthlyPrice: subscription?.monthlyPrice || 0,
    contractStartDate: subscription?.contractStart
      ? dayjs(subscription.contractStart).format("YYYY-MM-DD")
      : "",
    contractEndDate: subscription?.contractEnd
      ? dayjs(subscription.contractEnd).format("YYYY-MM-DD")
      : "",
    minimumTermMonths: subscription?.minimumTermMonths || 12,
    payedTill: subscription?.paidTill || "",
    trialEndDate: subscription?.trialEnd
      ? dayjs(subscription.trialEnd).format("YYYY-MM-DD")
      : "",
    discountPercent: subscription?.discount?.percent || 0,
    discountValidUntil: subscription?.discount?.validUntil
      ? dayjs(subscription.discount.validUntil).format("YYYY-MM-DD")
      : "",
    discountReason: subscription?.discount?.reason || "",
    notes: subscription?.notes || "",
  });

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Abo bearbeiten</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={formData.status} onChange={handleChange("status")} label="Status">
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Plan</InputLabel>
            <Select value={formData.plan} onChange={handleChange("plan")} label="Plan">
              <MenuItem value="basis">Basis</MenuItem>
              <MenuItem value="premium">Premium</MenuItem>
              <MenuItem value="elite">Elite</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Monatspreis"
            value={formData.monthlyPrice}
            onChange={handleChange("monthlyPrice")}
            inputProps={{ step: "0.01", min: 0 }}
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Vertragsbeginn"
            value={formData.contractStartDate}
            onChange={handleChange("contractStartDate")}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Vertragsende"
            value={formData.contractEndDate}
            onChange={handleChange("contractEndDate")}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Mindestlaufzeit Monate"
            value={formData.minimumTermMonths}
            onChange={handleChange("minimumTermMonths")}
            inputProps={{ min: 0 }}
          />

          <TextField
            fullWidth
            size="small"
            label="Bezahlt bis (MM/YYYY)"
            value={formData.payedTill}
            onChange={handleChange("payedTill")}
            placeholder="MM/YYYY"
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Testphase bis"
            value={formData.trialEndDate}
            onChange={handleChange("trialEndDate")}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Rabatt %"
            value={formData.discountPercent}
            onChange={handleChange("discountPercent")}
            inputProps={{ step: "0.01", min: 0, max: 100 }}
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Rabatt gültig bis"
            value={formData.discountValidUntil}
            onChange={handleChange("discountValidUntil")}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            size="small"
            label="Rabattgrund"
            value={formData.discountReason}
            onChange={handleChange("discountReason")}
          />

          <TextField
            fullWidth
            size="small"
            label="Notizen"
            value={formData.notes}
            onChange={handleChange("notes")}
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function SepaExportDialog({ open, onClose, onExport }) {
  const { data: bankingAccounts = [], isLoading: isLoadingAccounts } = useGetBankingAccountsQuery();

  const [bookMonth, setBookMonth] = useState(dayjs().add(1, "month").format("MM/YYYY"));
  const [markAsCollected, setMarkAsCollected] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  // Haupt-Account oder ersten Account als Default wählen
  useEffect(() => {
    if (bankingAccounts.length > 0 && !selectedAccountId) {
      const mainAccount = bankingAccounts.find((acc) => acc.isMain);
      setSelectedAccountId(mainAccount?._id || bankingAccounts[0]._id);
    }
  }, [bankingAccounts, selectedAccountId]);

  const handleExport = () => {
    const selectedAccount = bankingAccounts.find((acc) => acc._id === selectedAccountId);
    if (!selectedAccount) {
      alert("Bitte wählen Sie ein Bankkonto aus");
      return;
    }
    onExport(bookMonth, markAsCollected, selectedAccount);
  };

  const hasAccounts = bankingAccounts.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>SEPA-Export für Tenant-Abonnements</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Es werden automatisch Buchungen für alle Tenants erstellt, die für diesen Monat noch nicht bezahlt haben.
          </Alert>

          {isLoadingAccounts ? (
            <Alert severity="info">Bankkonten werden geladen...</Alert>
          ) : !hasAccounts ? (
            <Alert severity="warning">
              Keine Bankkonten gefunden. Bitte erstellen Sie zuerst ein Bankkonto in den Einstellungen.
            </Alert>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>Bankkonto (Gläubiger)</InputLabel>
              <Select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                label="Bankkonto (Gläubiger)"
              >
                {bankingAccounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {account.creditorName} - {account.creditorIBAN}
                      </Typography>
                      {account.isMain && (
                        <Chip size="small" label="Haupt" color="primary" sx={{ height: 20 }} />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            size="small"
            label="Buchungsmonat (MM/YYYY)"
            value={bookMonth}
            onChange={(e) => setBookMonth(e.target.value)}
            placeholder="MM/YYYY"
            helperText="Format: MM/YYYY (z.B. 03/2026)"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Nach Export als eingezogen markieren?</InputLabel>
            <Select
              value={markAsCollected}
              onChange={(e) => setMarkAsCollected(e.target.value)}
              label="Nach Export als eingezogen markieren?"
            >
              <MenuItem value={true}>Ja, automatisch als eingezogen markieren</MenuItem>
              <MenuItem value={false}>Nein, Status bleibt "pending"</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={<Iconify icon="mdi:download" width={18} />}
          disabled={!hasAccounts || isLoadingAccounts}
        >
          SEPA-XML herunterladen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SubscriptionsTab() {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sepaDialogOpen, setSepaDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const { data: rawSubscriptions = [], isLoading, isError, refetch } = useGetSubscriptionsQuery();

  // Normalize API shape: flatten subscription fields and nest tenant info
  const subscriptions = useMemo(() =>
    rawSubscriptions.map((s) => ({
      tenant: { _id: s._id, tenantName: s.tenantName },
      plan: s.plan,
      status: s.subscription?.status || "active",
      monthlyPrice: s.subscription?.monthlyPrice || 0,
      paidTill: s.subscription?.paidTill || "",
      contractStart: s.subscription?.contractStart || null,
      contractEnd: s.subscription?.contractEnd || null,
      cancelledAt: s.subscription?.cancelledAt || null,
      minimumTermMonths: s.subscription?.minimumTermMonths || 12,
      trialEnd: s.subscription?.trialEnd || null,
      discount: s.subscription?.discount || {},
      terminationReason: s.subscription?.terminationReason || "",
      notes: s.subscription?.notes || "",
      billingIban: s.billingIban || "",
      billingAccountHolder: s.billingAccountHolder || "",
      billingContactName: s.billingContactName || "",
      sepaConsentAt: s.sepaConsentAt || null,
    })),
  [rawSubscriptions]);
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [getSepaExportData] = useGetSepaExportDataMutation();
  const [markBookingsCollected] = useMarkBookingsCollectedMutation();

  const filteredSubscriptions = useMemo(() => {
    const normalizedSearch = String(searchValue || "").trim().toLowerCase();
    if (!normalizedSearch) return subscriptions;
    return subscriptions.filter((sub) => {
      const tenantName = sub?.tenant?.tenantName || "";
      return tenantName.toLowerCase().includes(normalizedSearch);
    });
  }, [searchValue, subscriptions]);

  const handleEditOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedSubscription(null);
  };

  const handleEditSave = async (formData) => {
    try {
      const payload = {
        tenantId: selectedSubscription.tenant._id,
        billingPlan: formData.plan,
        status: formData.status,
        monthlyPrice: Number(formData.monthlyPrice) || 0,
        minimumTermMonths: Number(formData.minimumTermMonths) || 12,
        paidTill: formData.payedTill || null,
        notes: formData.notes || "",
      };
      if (formData.contractStartDate) payload.contractStart = formData.contractStartDate;
      if (formData.contractEndDate) payload.contractEnd = formData.contractEndDate;
      if (formData.trialEndDate) payload.trialEnd = formData.trialEndDate;
      if (formData.discountPercent || formData.discountValidUntil || formData.discountReason) {
        payload.discount = {
          percent: Number(formData.discountPercent) || 0,
          validUntil: formData.discountValidUntil || null,
          reason: formData.discountReason || "",
        };
      }
      await updateSubscription(payload).unwrap();
      handleEditClose();
    } catch (error) {
      console.error("Failed to update subscription:", error);
      alert("Fehler beim Aktualisieren des Abos");
    }
  };

  const handleSepaExport = async (bookMonth, markAsCollected, selectedAccount) => {
    // Validierung bookMonth Format (MM/YYYY)
    const bookMonthRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
    if (!bookMonthRegex.test(bookMonth)) {
      dispatch(
        setMessage({
          message: "Ungültiges Format für Buchungsmonat. Bitte verwenden Sie MM/YYYY (z.B. 03/2026)",
          type: "error",
        })
      );
      return;
    }

    // Validierung Bankkonto
    if (!selectedAccount || !selectedAccount.creditorIBAN || !selectedAccount.creditorId) {
      dispatch(
        setMessage({
          message: "Bitte wählen Sie ein gültiges Bankkonto mit IBAN und Gläubiger-ID aus.",
          type: "error",
        })
      );
      return;
    }

    try {
      const result = await getSepaExportData({ bookMonth }).unwrap();

      // Prüfen ob Zahlungen vorhanden sind
      if (!result.payments || result.payments.length === 0) {
        dispatch(
          setMessage({
            message: `Keine ausstehenden Zahlungen für ${bookMonth} gefunden.`,
            type: "warning",
          })
        );
        setSepaDialogOpen(false);
        return;
      }

      // Creditor-Daten aus ausgewähltem Bankkonto
      const creditor = {
        creditorName: selectedAccount.creditorName,
        creditorIBAN: selectedAccount.creditorIBAN,
        creditorBIC: selectedAccount.creditorBIC || "",
        creditorId: selectedAccount.creditorId,
      };

      const collectionDate = dayjs().add(7, "day").format("YYYY-MM-DD");

      const payments = result.payments.map((p) => ({
        debtorName: p.debtorName || p.tenantName,
        iban: p.iban || "",
        bic: p.bic || "",
        amount: p.amount,
        mandateId: p.mandateId || `MA-${p.tenantId}`,
        mandateDate: p.mandateDate || dayjs().format("YYYY-MM-DD"),
        remittance: p.remittance || `Martial Artministration ${p.plan} ${bookMonth}`,
      }));

      // XML generieren
      const xmlData = buildPain008Xml({ creditor, collectionDate, payments });

      if (!xmlData) {
        dispatch(
          setMessage({
            message: "Fehler beim Erstellen der SEPA-XML-Datei",
            type: "error",
          })
        );
        return;
      }

      // XML-Datei herunterladen
      const blob = new Blob([xmlData], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `SEPA_Mandanten_${bookMonth.replace("/", "_")}.xml`;
      link.click();
      URL.revokeObjectURL(url);

      // Buchungen als eingezogen markieren (wenn gewünscht)
      if (markAsCollected) {
        const bookingIds = result.payments
          .map((p) => p.bookingId)
          .filter((id) => id); // Nur gültige IDs

        if (bookingIds.length > 0) {
          try {
            await markBookingsCollected({ bookingIds }).unwrap();
            dispatch(
              setMessage({
                message: `SEPA-Export erfolgreich! ${result.count} Zahlungen exportiert und als eingezogen markiert.`,
                type: "success",
              })
            );
          } catch (markError) {
            console.error("Failed to mark bookings as collected:", markError);
            dispatch(
              setMessage({
                message: `SEPA-Export erfolgreich (${result.count} Zahlungen), aber Fehler beim Markieren als eingezogen: ${markError?.data?.message || markError.message}`,
                type: "warning",
              })
            );
          }
        } else {
          dispatch(
            setMessage({
              message: `SEPA-Export erfolgreich! ${result.count} Zahlungen exportiert (keine Buchungs-IDs zum Markieren gefunden).`,
              type: "success",
            })
          );
        }
      } else {
        dispatch(
          setMessage({
            message: `SEPA-Export erfolgreich! ${result.count} Zahlungen exportiert (Status bleibt "pending").`,
            type: "success",
          })
        );
      }

      setSepaDialogOpen(false);
      refetch(); // Subscriptions neu laden
    } catch (error) {
      console.error("Failed to export SEPA:", error);
      const errorMessage = error?.data?.message || error.message || "Unbekannter Fehler";
      dispatch(
        setMessage({
          message: `Fehler beim SEPA-Export: ${errorMessage}`,
          type: "error",
        })
      );
    }
  };

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={1}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Abonnements-Verwaltung
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Übersicht aller Mandanten-Abonnements
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Iconify icon="mdi:file-export" width={18} />}
              onClick={() => setSepaDialogOpen(true)}
            >
              SEPA-Export
            </Button>
          </Stack>

          <TextField
            fullWidth
            size="small"
            label="Mandant suchen"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Name des Mandanten"
          />
        </Stack>
      </Paper>

      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Erneut laden
            </Button>
          }
        >
          Abos konnten nicht geladen werden.
        </Alert>
      ) : null}

      {isLoading ? <Alert severity="info">Abos werden geladen...</Alert> : null}

      {!isLoading && !isError && filteredSubscriptions.length === 0 ? (
        <Alert severity="warning">Keine Abos passend zur Suche gefunden.</Alert>
      ) : null}

      {!isLoading && !isError && filteredSubscriptions.length > 0 ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mandant</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Monatspreis</TableCell>
                  <TableCell>Bezahlt bis</TableCell>
                  <TableCell>Vertragsbeginn</TableCell>
                  <TableCell>Vertragsende</TableCell>
                  <TableCell>Gekündigt am</TableCell>
                  <TableCell align="right">Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubscriptions.map((sub) => (
                  <TableRow
                    key={sub.tenant._id}
                    hover
                    sx={{
                      bgcolor:
                        sub.status === "active"
                          ? (theme) => alpha(theme.palette.success.main, 0.05)
                          : sub.status === "cancelled"
                            ? (theme) => alpha(theme.palette.warning.main, 0.08)
                          : "inherit",
                    }}
                  >
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="subtitle2">
                          {sub?.tenant?.tenantName || "Unbekannt"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sub?.tenant?._id || "-"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={sub.plan || "basis"} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={getStatusColor(sub.status)}
                        label={getStatusLabel(sub.status)}
                      />
                    </TableCell>
                    <TableCell>{sub.monthlyPrice?.toFixed(2) || "0.00"} €</TableCell>
                    <TableCell>{sub.paidTill || "-"}</TableCell>
                    <TableCell>
                      {sub.contractStart
                        ? dayjs(sub.contractStart).format("DD.MM.YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {sub.contractEnd
                        ? dayjs(sub.contractEnd).format("DD.MM.YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {sub.cancelledAt
                        ? dayjs(sub.cancelledAt).format("DD.MM.YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEditOpen(sub)}
                        startIcon={<Iconify icon="mdi:pencil" width={14} />}
                      >
                        Bearbeiten
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      {editDialogOpen && selectedSubscription && (
        <EditSubscriptionDialog
          open={editDialogOpen}
          onClose={handleEditClose}
          subscription={selectedSubscription}
          onSave={handleEditSave}
        />
      )}

      {sepaDialogOpen && (
        <SepaExportDialog
          open={sepaDialogOpen}
          onClose={() => setSepaDialogOpen(false)}
          onExport={handleSepaExport}
        />
      )}
    </Stack>
  );
}
